/* Save for later — Shopping Halal custom cart feature.
 * Adds a "Save for later" link to every cart line (cart page + cart drawer) and a
 * "Saved for later" list under the cart. Saved items are kept in this browser (localStorage).
 * Saving removes the line through the theme's own cart-items-component; "Move to cart"
 * adds it back and lets the theme re-render the cart. */
const KEY = 'sh_saved_for_later_v1';

const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]') || []; } catch (e) { return []; } };
const store = (items) => { try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (e) {} };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const getCart = async () => (await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })).json();
const money = (cents, currency) => {
  try { return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: currency || 'USD' }); }
  catch (e) { return '$' + (cents / 100).toFixed(2); }
};

function decorateRows() {
  document.querySelectorAll('cart-items-component tr.cart-items__table-row[data-key]').forEach((row) => {
    if (row.dataset.parentKey) return; // nested bundle lines
    const cell = row.querySelector('.cart-items__details');
    if (!cell || cell.querySelector('[data-sfl-save]')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cart-items__save-later';
    btn.setAttribute('data-sfl-save', row.dataset.key);
    btn.textContent = 'Save for later';
    cell.appendChild(btn);
  });
  // Custom header mini cart (snippets/shb-mini-cart.liquid)
  document.querySelectorAll('#shbMiniCart .shb-minicart__line[data-line-key]').forEach((line) => {
    const info = line.querySelector('.shb-minicart__line-info');
    if (!info || info.querySelector('[data-sfl-save]')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cart-items__save-later sfl__mini-save';
    btn.setAttribute('data-sfl-save', line.getAttribute('data-line-key'));
    btn.textContent = 'Save for later';
    info.appendChild(btn);
  });
}

function renderLists() {
  const items = load();
  const sig = JSON.stringify(items);
  document.querySelectorAll('cart-items-component .cart-items__wrapper, #shbMiniCart [data-cart-body]').forEach((wrapper) => {
    let el = wrapper.querySelector(':scope > [data-sfl-list]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'sfl';
      el.setAttribute('data-sfl-list', '');
      wrapper.appendChild(el);
    }
    if (el.dataset.sflSig === sig) return;
    el.dataset.sflSig = sig;
    if (!items.length) { el.hidden = true; el.innerHTML = ''; return; }
    el.hidden = false;
    el.innerHTML =
      `<h3 class="sfl__heading">Saved for later (${items.length})</h3><ul class="sfl__list">` +
      items.map((it, i) => `
        <li class="sfl__item">
          ${it.image ? `<a href="${esc(it.url)}" class="sfl__media"><img src="${esc(it.image)}" alt="${esc(it.title)}" loading="lazy" width="64" height="64"></a>` : ''}
          <div class="sfl__info">
            <a href="${esc(it.url)}" class="sfl__title">${esc(it.title)}</a>
            ${it.plan ? `<span class="sfl__meta">${esc(it.plan)}</span>` : ''}
            <span class="sfl__price">${esc(it.price)}${it.quantity > 1 ? ` &times; ${it.quantity}` : ''}</span>
            <div class="sfl__actions">
              <button type="button" class="sfl__btn sfl__btn--primary" data-sfl-move="${i}">Move to cart</button>
              <button type="button" class="sfl__btn" data-sfl-delete="${i}">Remove</button>
            </div>
          </div>
        </li>`).join('') + '</ul>';
  });
}

// Free-shipping reminder above the Checkout button on the cart page ($150 threshold,
// same as the header mini cart). Shopify's own checkout pages can't be edited on this plan.
const FREE_SHIP_CENTS = 9900;
function renderShipBars() {
  document.querySelectorAll('.cart__ctas').forEach((ctas) => {
    const scope = ctas.closest('cart-items-component') || document;
    const totalEl = scope.querySelector('[data-testid="cart-total-value"]');
    if (!totalEl) return;
    const raw = (totalEl.getAttribute('value') || totalEl.textContent || '').replace(/[^0-9.]/g, '');
    const cents = Math.round(parseFloat(raw) * 100);
    if (!Number.isFinite(cents)) return;
    let bar = ctas.previousElementSibling;
    if (!bar || !bar.matches('[data-sfl-ship]')) {
      bar = document.createElement('div');
      bar.className = 'sfl-ship';
      bar.setAttribute('data-sfl-ship', '');
      ctas.before(bar);
    }
    const sig = String(cents);
    if (bar.dataset.sig === sig) return;
    bar.dataset.sig = sig;
    const pct = Math.max(0, Math.min(100, Math.round((cents * 100) / FREE_SHIP_CENTS)));
    const free = cents >= FREE_SHIP_CENTS;
    bar.classList.toggle('is-free', free);
    bar.innerHTML =
      `<p class="sfl-ship__msg">${free
        ? 'You&rsquo;ve unlocked <strong>FREE shipping!</strong>'
        : `Add <strong>${money(FREE_SHIP_CENTS - cents)}</strong> more to get <strong>FREE shipping</strong>`}</p>` +
      `<div class="sfl-ship__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}"><span style="width:${pct}%"></span></div>`;
  });
}

function refresh() { decorateRows(); renderLists(); renderShipBars(); }

async function refreshCart(lines) {
  // Header mini cart + count bubble
  try {
    const cart = await getCart();
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
    const mini = document.getElementById('shbMiniCart');
    if (mini && mini.classList.contains('is-open') && typeof window.shbOpenMiniCart === 'function') window.shbOpenMiniCart();
  } catch (e) {}
  try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines, promise: deferred.promise }));
    const cart = await getCart();
    deferred.resolve({
      cart: typeof CartLinesUpdateEvent.createCartFromAjaxResponse === 'function' ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : cart,
      detail: { items: cart.items, itemCount: cart.item_count, source: 'save-for-later', didError: false },
    });
  } catch (e) {
    location.reload();
  }
}

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const saveBtn = target.closest('[data-sfl-save]');
  if (saveBtn) {
    event.preventDefault();
    saveBtn.disabled = true;
    try {
      const key = saveBtn.getAttribute('data-sfl-save');
      const cart = await getCart();
      const index = cart.items.findIndex((it) => it.key === key);
      if (index < 0) { location.reload(); return; }
      const it = cart.items[index];
      const planId = it.selling_plan_allocation?.selling_plan?.id || '';
      const items = load().filter((s) => !(String(s.variantId) === String(it.variant_id) && String(s.planId || '') === String(planId)));
      items.unshift({
        variantId: it.variant_id,
        planId,
        plan: it.selling_plan_allocation?.selling_plan?.name || '',
        quantity: it.quantity,
        title: it.product_title,
        url: it.url,
        image: it.image ? it.image + (it.image.includes('?') ? '&' : '?') + 'width=160' : '',
        price: money(it.final_price, cart.currency),
        savedAt: Date.now(),
      });
      store(items);
      const miniLine = saveBtn.closest('#shbMiniCart [data-line-key]');
      const miniRemove = miniLine && miniLine.querySelector('[data-line-remove]');
      const component = saveBtn.closest('cart-items-component');
      if (miniRemove) {
        miniRemove.click();
      } else if (component && typeof component.onLineItemRemove === 'function') {
        component.onLineItemRemove(index + 1);
      } else {
        await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ line: index + 1, quantity: 0 }) });
        location.reload();
      }
      renderLists();
    } catch (e) {
      saveBtn.disabled = false;
      console.warn('[save-for-later]', e);
    }
    return;
  }

  const moveBtn = target.closest('[data-sfl-move]');
  if (moveBtn) {
    event.preventDefault();
    const items = load();
    const idx = Number(moveBtn.dataset.sflMove);
    const it = items[idx];
    if (!it) return;
    moveBtn.disabled = true;
    const line = { id: Number(it.variantId), quantity: it.quantity || 1 };
    if (it.planId) line.selling_plan = Number(it.planId);
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [line] }),
    });
    if (!res.ok) {
      moveBtn.disabled = false;
      let msg = 'Sorry, this item could not be added to your cart.';
      try { msg = (await res.json()).description || msg; } catch (e) {}
      alert(msg);
      return;
    }
    items.splice(idx, 1);
    store(items);
    renderLists();
    refreshCart([{ merchandiseId: `gid://shopify/ProductVariant/${it.variantId}`, quantity: it.quantity || 1 }]);
    return;
  }

  const delBtn = target.closest('[data-sfl-delete]');
  if (delBtn) {
    event.preventDefault();
    const items = load();
    items.splice(Number(delBtn.dataset.sflDelete), 1);
    store(items);
    renderLists();
  }
});

// The theme morphs/replaces cart markup on every update; re-apply our UI afterwards.
let scheduled = false;
new MutationObserver(() => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    document.querySelectorAll('[data-sfl-list]').forEach((el) => { if (!el.innerHTML.trim() && load().length) delete el.dataset.sflSig; });
    refresh();
  });
}).observe(document.documentElement, { childList: true, subtree: true });

// Fix for the header mini cart: Shopify's /cart/change.js response now reports total_price
// including the estimated shipping, and the mini cart renders straight from that response
// (e.g. $145.20 showed as $180.19 and the bar said "FREE shipping unlocked").
// Re-render it from /cart.js, which has the real item total.
document.addEventListener('cart:updated', () => {
  const mini = document.getElementById('shbMiniCart');
  if (mini && mini.classList.contains('is-open') && typeof window.shbOpenMiniCart === 'function') window.shbOpenMiniCart();
});

window.addEventListener('storage', (e) => {
  if (e.key !== KEY) return;
  document.querySelectorAll('[data-sfl-list]').forEach((el) => delete el.dataset.sflSig);
  renderLists();
});

refresh();
